// El sheet de la interfaz nueva en el TELÉFONO (ADR-2026-10-08-la-interfaz-nueva-llega-al-telefono):
// lo que en escritorio son las cards por destino de la home y los paneles de la sidebar
// (Redes sociales, Catálogo, Herramientas), acá es un sheet desde abajo. Lo abren el botón
// central de la barra (en el paso de los destinos), la pestaña Herramientas y las cards de
// la home (ya en su sección).
//
// Los modos y las ediciones llegan resueltos de `useModosGenerar` —candado por plan, visor
// de ejemplos, a dónde navega cada uno—, los MISMOS que usa `SheetGenerar` para el resto de
// los grupos. Lo que cambia es cómo se agrupan: por destino y no por imagen / video, con la
// misma lista de modos por sección que la sidebar (`seccionesDeModos`).
//
// Redes sociales y Catálogo se ven IGUAL que en escritorio
// (ADR-2026-10-12-el-sheet-del-telefono-con-las-cards-de-escritorio): Crear y Recrear, con
// las mismas cards (`GrillaDeModos`: carátula 3:2 con el tinte de la sección, nombre y
// bajada). Los destinos ya no ofrecen el Agente: tiene su lugar en la barra.

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import type { EdicionSheet, ModoSheet } from './SheetGenerar';
import { CardDestinoMobile } from './CardDestinoMobile';
import { DESTINOS_OCULTOS } from './ordenDeDestinos';
import { CardDeHerramientas, GrillaDeHerramientas } from './HerramientasDeCrear';
import { PantallaDePublicar } from './PantallaDePublicar';
import { GrillaDeModos } from './GrillaDeModos';
import { CardModoProximo } from '../../estilos/CardModoProximo';
import type { ModoHome } from '../../estilos/EstiloMinimal';
import {
  caratulaConTinte,
  tieneCaratulaDelRubro,
  modosDeCatalogoDelRubro,
  MODOS_DE_HERRAMIENTAS,
  modosDeRedesDelRubro,
  modoPrincipalDeLaSeccion,
  PROXIMOS_DE_CATALOGO,
  proximosQueQuedan,
} from '../../estilos/seccionesDeModos';
import type { SeccionDelSheet } from './shellMobile';
import { bajadaCortaModo, caratulaModo, nombreCortoModo, portadaDeRubro } from '../modesCatalog';
import { iconoDeHerramienta } from '../../estilos/contenidoAtlas';

/**
 * Crear, si la lista de modos no lo trae: la misma card que arma la home de escritorio
 * (`modosHomeEscritorio` en `InterfazV2`). Sin candado: la imagen es de todos los planes y
 * el video lo frena la pantalla al generar.
 */
const crearComoModo = (abrir: () => void): ModoSheet => ({
  path: 'redes',
  titulo: nombreCortoModo('redes'),
  imagen: caratulaModo('redes').poster,
  familia: 'imagen',
  bloqueado: false,
  onAbrir: abrir,
});

/**
 * Herramientas que se listan pero no se abren en el teléfono: su pantalla dice "Disponible en
 * computadora". Se muestran igual —la lista es la MISMA que la de escritorio— con esa nota y
 * sin toque, así no llevan a una pantalla muerta.
 */
const SOLO_EN_COMPUTADORA = new Set(['diseno-indumentaria']);

const TITULO: Record<Exclude<SeccionDelSheet, null>, string> = {
  redes: 'Redes sociales',
  catalogo: 'Catálogo',
  herramientas: 'Herramientas',
  publicar: 'Publicar',
};

/**
 * Los modos de Herramientas en el orden de la sidebar, sin los que esta cuenta no ve. Lo usan
 * este sheet y la home del teléfono, así las dos listan lo mismo.
 */
export const modosDeHerramientasMobile = (modos: ModoSheet[], extra: ModoSheet[]): ModoSheet[] =>
  enOrden([...modos, ...extra], MODOS_DE_HERRAMIENTAS);

/** Los modos de la lista en ese orden, sin los que esta cuenta no ve. */
const enOrden = (modos: ModoSheet[], paths: readonly string[]) =>
  paths.flatMap((p) => {
    const m = modos.find((x) => x.path === p);
    return m ? [m] : [];
  });

export function SheetPorDestino({
  modos,
  herramientasExtra = [],
  ediciones,
  rubroMarca,
  seccionInicial,
  onClose,
}: {
  modos: ModoSheet[];
  /** Herramientas que no son modos (Flujos), de `useModosGenerar`. */
  herramientasExtra?: ModoSheet[];
  ediciones: EdicionSheet[];
  /** El rubro de la marca: decide la portada del rubro, que gana sobre la carátula teñida. */
  rubroMarca: string | null;
  /** Dónde abre: los destinos (`null`) o una sección ya elegida. */
  seccionInicial: SeccionDelSheet;
  onClose: () => void;
}) {
  // Entrada animada y body sin scroll: el mismo patrón que `SheetGenerar`.
  const [animado, setAnimado] = useState(false);
  useEffect(() => {
    const id = window.requestAnimationFrame(() => setAnimado(true));
    return () => window.cancelAnimationFrame(id);
  }, []);
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const [seccion, setSeccion] = useState<SeccionDelSheet>(seccionInicial);
  // Volver sólo existe si se llegó desde los destinos: abierto directo en una sección
  // (la pestaña Herramientas, una card de la home), la flecha cerraría sin sentido.
  const [desdeDestinos, setDesdeDestinos] = useState(seccionInicial === null);
  const [avanzando, setAvanzando] = useState(true);

  /**
   * El alto del sheet sigue al de su contenido con transición: al entrar a una sección más
   * larga (Herramientas, 11 cards) saltaba de golpe de tres cards a casi toda la pantalla
   * (ADR-2026-10-30-el-sheet-de-crear-cambia-de-seccion-sin-saltos). Se mide el contenido y
   * se le da ese alto en px al sheet: `auto` no se puede animar. El `max-h` sigue
   * mandando, y pasado ese tope el sheet scrollea como antes.
   */
  const sheetRef = useRef<HTMLDivElement>(null);
  const contenidoRef = useRef<HTMLDivElement>(null);
  const [alto, setAlto] = useState<number | null>(null);
  useLayoutEffect(() => {
    const sheet = sheetRef.current;
    const contenido = contenidoRef.current;
    if (!sheet || !contenido) return;
    // Topado al `max-h` acá y no sólo por CSS: animando hacia un alto mayor que el tope, la
    // curva lo cruza en el primer cuadro y el sheet saltaba igual.
    const medir = () => {
      const estilo = getComputedStyle(sheet);
      const tope = parseFloat(estilo.maxHeight) || Infinity;
      setAlto(Math.min(contenido.offsetHeight + parseFloat(estilo.paddingBottom || '0'), tope));
    };
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(contenido);
    // El tope sale de `100dvh`, que cambia sin que cambie el contenido (el navegador esconde su
    // barra al scrollear, se rota el teléfono): sin esto el sheet quedaba con el tope viejo
    // (review Greptile #231).
    window.addEventListener('resize', medir);
    window.visualViewport?.addEventListener('resize', medir);
    return () => {
      observador.disconnect();
      window.removeEventListener('resize', medir);
      window.visualViewport?.removeEventListener('resize', medir);
    };
  }, []);
  // Cada sección arranca arriba: si no, al volver de Herramientas scrolleado, los destinos
  // aparecían cortados.
  useLayoutEffect(() => {
    if (sheetRef.current) sheetRef.current.scrollTop = 0;
  }, [seccion]);

  const volver = () => {
    setAvanzando(false);
    setSeccion(null);
  };
  const entrar = (s: Exclude<SeccionDelSheet, null>) => {
    setAvanzando(true);
    setDesdeDestinos(true);
    setSeccion(s);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (seccion && desdeDestinos) volver();
      else onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [seccion, desdeDestinos, onClose]);

  // Cerrar SIEMPRE al elegir, también con candado: `onAbrir` levanta el visor de
  // ejemplos o lleva a planes, y quedarían debajo de este sheet.
  const abrir = (onAbrir: () => void) => {
    onAbrir();
    onClose();
  };

  const navigate = useNavigate();
  // Crear y Recrear, como la sidebar de escritorio. Los nombres ("Crear", "Recrear") los
  // pone `GrillaDeModos` con `uiNueva`.
  // Sin plantillas en el rubro, Replicar imagen ocupa el lugar de Recrear
  // (ADR-2026-10-12-replicar-imagen-en-lugar-de-recrear).
  const conCrear = modos.some((m) => m.path === 'redes') ? modos : [...modos, crearComoModo(() => navigate('/redes'))];
  const [crear, ...recrear] = enOrden(
    conCrear,
    modosDeRedesDelRubro((p) => conCrear.some((m) => m.path === p)),
  );
  // "Imagen automática" primero, como en el popup de escritorio
  // (ADR-2026-10-12-imagen-automatica-en-el-popup-de-redes-2): abre Crear con ese modo
  // elegido, con el candado de Crear y la carátula del modo Automático (`auto`).
  // El modo Automático propiamente dicho, no Crear (ADR-2026-10-12-imagen-automatica-abre-su-modo);
  // sin él en la lista, el camino de antes por Crear.
  const autoDelCatalogo = modos.find((m) => m.path === 'auto');
  const automatica: ModoSheet | null = autoDelCatalogo
    ? { ...autoDelCatalogo, titulo: 'Imagen automática' }
    : crear && crear.path === 'redes'
      ? {
          ...crear,
          path: 'auto',
          titulo: 'Imagen automática',
          onAbrir: crear.bloqueado ? crear.onAbrir : () => navigate('/redes', { state: { modo: 'automatico' } }),
        }
      : null;
  const modosDeRedes: ModoSheet[] = automatica
    ? [automatica, ...recrear, crear]
    : [crear, ...recrear].filter((m): m is ModoSheet => !!m);
  // Los rubros no principales, sólo el Catálogo con el agente
  // (ADR-2026-10-26-catalogo-con-el-agente-para-los-rubros-no-principales).
  const modosDeCatalogo = enOrden(modos, modosDeCatalogoDelRubro(rubroMarca));
  // Sin el anuncio de un modo que ya está en la lista (hoy `PROXIMOS_DE_CATALOGO` está vacío).
  const proximosDeCatalogo = proximosQueQuedan(PROXIMOS_DE_CATALOGO, modosDeCatalogo.map((m) => m.path));
  /** Lo que dibuja la grilla de escritorio; el toque cierra el sheet (`abrir`). */
  const aModoHome = (m: ModoSheet): ModoHome => ({
    path: m.path,
    titulo: m.titulo,
    imagen: m.imagen,
    bloqueado: m.bloqueado,
    conEjemplos: m.conEjemplos,
    familia: m.familia,
    onAbrir: () => abrir(m.onAbrir),
  });
  // La misma lista que el panel de la sidebar y la home de escritorio.
  const modosDeHerramientas = modosDeHerramientasMobile(modos, herramientasExtra);

  // La pestaña Publicar: Instagram y Tienda online, a pantalla completa
  // (ADR-2026-10-30-publicar-reemplaza-a-herramientas-en-la-barra).
  if (seccionInicial === 'publicar') {
    return <PantallaDePublicar onElegir={(ruta) => abrir(() => navigate(ruta))} />;
  }

  return createPortal(
    <>
      <div
        aria-hidden="true"
        onClick={onClose}
        className={`fixed inset-0 z-[69] bg-black/40 transition-opacity duration-300 md:hidden ${
          animado ? 'opacity-100' : 'opacity-0'
        }`}
      />
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={seccion ? TITULO[seccion] : '¿Qué creamos hoy?'}
        className={`fixed inset-x-0 bottom-0 z-[70] max-h-[calc(100dvh-env(safe-area-inset-top,0px)-0.5rem)] overflow-y-auto overscroll-contain rounded-t-[24px] bg-white shadow-[0_-8px_40px_rgb(var(--brand-black-rgb)_/_0.18)] motion-reduce:!transition-none md:hidden ${
          animado ? 'translate-y-0' : 'translate-y-full'
        }`}
        style={{
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)',
          height: alto ?? undefined,
          // La entrada desde abajo, como siempre; el alto, con la curva del contenido.
          transition: 'transform 300ms ease-out, height 420ms cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        {/* Lo que se mide para el alto del sheet. */}
        <div ref={contenidoRef}>
          <div aria-hidden="true" className="flex justify-center pb-1 pt-2.5">
            <span className="h-1 w-9 rounded-full bg-black/15" />
          </div>

          <div
            key={seccion ?? 'destinos'}
            className={avanzando ? 'animate-seccion-del-sheet' : 'animate-seccion-del-sheet-back'}
          >
          {seccion === null ? (
            /* ── Los destinos ─────────────────────────────────────────── */
            <div className="flex flex-col gap-3 px-4 pb-1 pt-3">
              <div className="flex items-center justify-between gap-3 px-1 pb-1">
                <h1 className="text-[20px] font-bold leading-[26px] text-[#18181B]">¿Qué creamos hoy?</h1>
                <BotonCerrar onClose={onClose} />
              </div>
              <CardDestinoMobile seccion="redes" onClick={() => entrar('redes')} />
              <CardDestinoMobile seccion="catalogos" onClick={() => entrar('catalogo')} />
              {/* Oculto mientras no tenga modos (ADR-2026-10-26-anuncios-se-oculta-hasta-tener-modos). */}
              {!DESTINOS_OCULTOS.has('anuncios') && <CardDestinoMobile seccion="anuncios" onClick={() => {}} />}
              {/* Herramientas: antes era un lugar de la barra, que ahora es de Publicar. Entra
                  como Redes sociales y Catálogo (ADR-2026-10-30-herramientas-se-abre-como-catalogo). */}
              <CardDeHerramientas ediciones={ediciones} modos={modosDeHerramientas} onClick={() => entrar('herramientas')} />
            </div>
          ) : (
            /* ── Una sección ──────────────────────────────────────────── */
            <div className="flex flex-col gap-2 pb-1">
              <div className="sticky top-0 z-20 flex items-center justify-between bg-white px-4 pb-2 pt-1.5">
                {desdeDestinos ? (
                  <button
                    type="button"
                    onClick={volver}
                    aria-label="Volver"
                    className="grid h-9 w-9 place-items-center rounded-full text-[#16181D]/70 active:opacity-60"
                  >
                    <span className="material-symbols-rounded text-[24px]">arrow_back</span>
                  </button>
                ) : (
                  <span className="h-9 w-9" aria-hidden="true" />
                )}
                <span className="text-[17px] font-bold text-[#18181B]">{TITULO[seccion]}</span>
                <BotonCerrar onClose={onClose} />
              </div>

              {seccion === 'herramientas' ? (
                // Las mismas cards que Redes sociales y Catálogo
                // (ADR-2026-10-30-herramientas-se-abre-como-catalogo).
                <GrillaDeHerramientas ediciones={ediciones} modos={modosDeHerramientas} onElegir={abrir} />
              ) : (
                // Las MISMAS cards que el panel de la sidebar de escritorio: carátula 3:2 con
                // el fondo de la sección, nombre y bajada.
                <GrillaDeModos
                  uiNueva
                  modos={(seccion === 'redes' ? modosDeRedes : modosDeCatalogo).map(aModoHome)}
                  rubroMarca={rubroMarca}
                  onAbrir={(m) => m.onAbrir()}
                  className="grid grid-cols-2 gap-3 px-4"
                  // El modo principal, arriba a lo ancho (ADR-2026-10-31-el-modo-principal-de-cada-seccion).
                  destacado={seccion === 'redes' || seccion === 'catalogo' ? modoPrincipalDeLaSeccion(seccion, (seccion === 'redes' ? modosDeRedes : modosDeCatalogo).map((m) => m.path)) : null}
                  caratulaPropia={(path) =>
                    portadaDeRubro(path, rubroMarca) &&
                    !tieneCaratulaDelRubro(seccion === 'redes' ? 'redes' : 'catalogo', path, rubroMarca)
                      ? undefined
                      : caratulaConTinte(seccion === 'redes' ? 'redes' : 'catalogo', path, rubroMarca)
                  }
                  alFinal={
                    seccion === 'catalogo'
                      ? proximosDeCatalogo.map((p) => <CardModoProximo key={p.nombre} nombre={p.nombre} bajada={p.bajada} />)
                      : undefined
                  }
                />
              )}
            </div>
          )}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

function BotonCerrar({ onClose }: { onClose: () => void }) {
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label="Cerrar"
      className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-[#16181D]/70 active:opacity-60"
    >
      <span className="material-symbols-rounded text-[24px]">close</span>
    </button>
  );
}

/**
 * Herramientas: las ediciones rápidas y después los modos de herramientas, una debajo de la
 * otra (ícono, nombre y qué hace) — la misma lista y los MISMOS íconos que el panel de la
 * sidebar y la sección de la home de escritorio, con la pastilla gris.
 */
export function ListaDeHerramientas({
  ediciones,
  modos,
  onElegir,
}: {
  ediciones: EdicionSheet[];
  modos: ModoSheet[];
  onElegir: (onAbrir: () => void) => void;
}) {
  const fila =
    'flex w-full min-w-0 items-center gap-3 rounded-2xl px-2 py-1.5 text-left active:bg-black/[0.04] disabled:active:bg-transparent';
  const pastilla =
    'relative grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-[11px] bg-[#F2F3F7] text-black/60 ring-1 ring-black/[0.04]';
  const candado = (
    <span aria-hidden="true" className="absolute inset-0 grid place-items-center bg-black/45 text-white">
      <span className="material-symbols-rounded text-[15px]">lock</span>
    </span>
  );
  return (
    <div className="flex flex-col gap-0.5 px-3">
      {ediciones.map((e) => (
        <button key={e.label} type="button" onClick={() => onElegir(e.onAbrir)} className={fila}>
          <span className={pastilla}>
            <e.Icono className="h-[18px] w-[18px]" />
            {e.bloqueado && candado}
          </span>
          <Texto nombre={e.label} bajada={e.bloqueado ? 'Incluido en los planes pagos' : e.desc} apagado={e.bloqueado} />
        </button>
      ))}
      {modos.map((m) => {
        const soloCompu = SOLO_EN_COMPUTADORA.has(m.path);
        const simbolo = iconoDeHerramienta(m.path);
        return (
          <button
            key={m.path}
            type="button"
            onClick={soloCompu ? undefined : () => onElegir(m.onAbrir)}
            disabled={soloCompu}
            className={fila}
          >
            <span className={`${pastilla} ${soloCompu ? 'opacity-50' : ''}`}>
              {simbolo ? (
                <span aria-hidden="true" className="material-symbols-rounded text-[20px]">
                  {simbolo}
                </span>
              ) : (
                <img src={m.imagenCuadrada ?? m.imagen} alt="" className="h-full w-full object-cover" />
              )}
              {m.bloqueado && !soloCompu && candado}
            </span>
            <Texto
              nombre={m.titulo}
              bajada={
                soloCompu
                  ? 'Disponible en computadora'
                  : m.bloqueado
                    ? 'Incluido en los planes pagos'
                    : bajadaCortaModo(m.path, '') || undefined
              }
              apagado={soloCompu || m.bloqueado}
            />
          </button>
        );
      })}
    </div>
  );
}

function Texto({ nombre, bajada, apagado }: { nombre: string; bajada?: string; apagado?: boolean }) {
  return (
    <span className="min-w-0 flex-1">
      <span className={`block truncate text-[15px] font-semibold ${apagado ? 'text-black/45' : 'text-[#16181D]'}`}>
        {nombre}
      </span>
      {bajada && <span className="mt-0.5 block truncate text-[12.5px] text-black/50">{bajada}</span>}
    </span>
  );
}
